import { ApiFetch } from '../../../shared/utils/fetch';
import { unwrap, type Enveloped } from '../../../shared/utils/unwrap';

/**
 * Merchant packages & add-ons configuration (PE-2103), against osprey-api's
 * `/packages` routes (add-ons land in osprey-api #70).
 * All routes are scoped server-side to the selected merchant via x-tenant-id
 * (ApiFetch derives it from selectedTenantId). Money is in minor units (kobo).
 *
 * NB: PE-2086 (osprey-api #70) renamed `/packages/templates` → `/packages/listings`
 * — same payload shape, new path.
 *
 * ⚠️ The old path does NOT 404, which is why this went unnoticed: `packages.controller`
 * declares a generic `@Get(':id')` (get a purchased package instance), so a request to
 * `/packages/templates` still matches — as `:id = "templates"` — and returns 401/an
 * instance-lookup error rather than an obvious "route gone". Probed 2026-08-13:
 * `/packages/templates` → 401, while a genuinely absent path like
 * `/sales/not-a-real-subroute` → 404. Don't use a non-404 to conclude a route survived.
 *
 * The `PackageTemplate` TS names are retained for now to keep the change surgical;
 * the backend's word for it is "listing".
 */

export interface PackageTemplate {
  id: string;
  name: string;
  /** Ordered step names the package walks a customer through. */
  steps: string[];
  /** Price in kobo (minor units). */
  price: number;
  description?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreatePackageTemplatePayload {
  name: string;
  steps: string[];
  price: number;
  description?: string;
}
export type UpdatePackageTemplatePayload = Partial<CreatePackageTemplatePayload>;

/** Add-on source — MERCHANT add-ons are merchant-authored; others (e.g. platform) are read-only here. */
export type AddOnSource = 'MERCHANT' | 'PLATFORM' | string;

export interface AddOn {
  id: string;
  name: string;
  /** Price in kobo (minor units). */
  price: number;
  /** Optional extra session time this add-on grants. */
  extraDurationMinutes?: number | null;
  /** Immutable once created. */
  source: AddOnSource;
  /** Template ids this add-on is attachable to. */
  packageIds: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateAddOnPayload {
  name: string;
  price: number;
  extraDurationMinutes?: number;
  packageIds?: string[];
}
export type UpdateAddOnPayload = Partial<CreateAddOnPayload>;

export const packagesApi = {
  // --- Package templates ---
  listTemplates: async (): Promise<PackageTemplate[]> => {
    const response = await ApiFetch.get<Enveloped<PackageTemplate[]>>('/packages/listings');
    return unwrap(response);
  },
  createTemplate: async (data: CreatePackageTemplatePayload): Promise<PackageTemplate> => {
    const response = await ApiFetch.post<Enveloped<PackageTemplate>>('/packages/listings', data);
    return unwrap(response);
  },
  updateTemplate: async (id: string, data: UpdatePackageTemplatePayload): Promise<PackageTemplate> => {
    // ApiFetch.put issues a PATCH (see shared/utils/fetch.ts) → PATCH /packages/listings/:id.
    const response = await ApiFetch.put<Enveloped<PackageTemplate>>(`/packages/listings/${id}`, data);
    return unwrap(response);
  },
  deleteTemplate: async (id: string): Promise<void> => {
    await ApiFetch.del<Enveloped<{ deleted: boolean }>>(`/packages/listings/${id}`);
  },

  // --- Add-ons (osprey-api #70) ---
  listAddOns: async (): Promise<AddOn[]> => {
    const response = await ApiFetch.get<Enveloped<AddOn[]>>('/packages/addons');
    return unwrap(response);
  },
  createAddOn: async (data: CreateAddOnPayload): Promise<AddOn> => {
    const response = await ApiFetch.post<Enveloped<AddOn>>('/packages/addons', data);
    return unwrap(response);
  },
  updateAddOn: async (id: string, data: UpdateAddOnPayload): Promise<AddOn> => {
    // source is immutable server-side; the form never sends it.
    const response = await ApiFetch.put<Enveloped<AddOn>>(`/packages/addons/${id}`, data);
    return unwrap(response);
  },
  removeAddOn: async (id: string): Promise<void> => {
    // Soft-archive; existing orders keep their snapshot.
    await ApiFetch.del<Enveloped<{ deleted: boolean }>>(`/packages/addons/${id}`);
  },
};
